/**
 * Provider home sign-in decisions, kept free of the page so they can be tested.
 * docs/provider-portal-consolidation.md.
 */
export const PROVIDER_DESTINATIONS = {
    admin: '/vrs-admin-dashboard.html',
    captioning: '/captioner-profile.html',
    jobs: '/interpreter-jobs.html',
    queue: '/interpreter-profile.html'
};
/**
 * The Jobs page opens on the interpreter side for a manager or superadmin who
 * asks for it (interpreter-jobs.js `readJobsSide`). They have no interpreter
 * account, so that side is a read-only view of an interpreter they pick (a
 * manager only within their own agency).
 */
export const INTERPRETER_SIDE_JOBS = `${PROVIDER_DESTINATIONS.jobs}?side=interpreter`;
const DESTINATION_LABELS = {
    adminDashboard: 'Admin dashboard',
    captioning: 'Captioning',
    interpreterView: 'Interpreter view',
    manageJobs: 'Manage jobs',
    myJobs: 'My jobs',
    queue: 'Queue'
};
/**
 * Where a signed-in provider can go, labelled by the side of Jobs they are
 * on: "Manage jobs" for a manager, "My jobs" for an interpreter. Managers and
 * superadmins see both sides. They also see the admin dashboard first: the
 * session from this sign-in is the dashboard's session (2026-10-26).
 */
export function providerDestinations(user) {
    if (user.role === 'admin' || user.role === 'superadmin') {
        return [
            { href: PROVIDER_DESTINATIONS.admin, id: 'admin', label: DESTINATION_LABELS.adminDashboard },
            { href: PROVIDER_DESTINATIONS.jobs, id: 'jobs', label: DESTINATION_LABELS.manageJobs },
            { href: INTERPRETER_SIDE_JOBS, id: 'jobs-interpreter', label: DESTINATION_LABELS.interpreterView }
        ];
    }
    if (user.role === 'captioner') {
        return [{ href: PROVIDER_DESTINATIONS.captioning, id: 'captioning', label: DESTINATION_LABELS.captioning }];
    }
    const modes = Array.isArray(user.serviceModes) ? user.serviceModes.map(mode => mode.toLowerCase()) : [];
    const destinations = [
        { href: PROVIDER_DESTINATIONS.queue, id: 'queue', label: DESTINATION_LABELS.queue }
    ];
    if (modes.includes('vri')) {
        destinations.push({ href: PROVIDER_DESTINATIONS.jobs, id: 'jobs', label: DESTINATION_LABELS.myJobs });
    }
    return destinations;
}
const PROVIDER_ROLES = ['interpreter', 'captioner'];
const CHOICE_ROLES = ['admin', 'superadmin', 'interpreter', 'captioner'];
const CHOICE_LABELS = {
    admin: 'Manager',
    captioner: 'Captioner',
    interpreter: 'Interpreter',
    superadmin: 'Superadmin'
};
/** The valid roles of a directory role choice, in the order the server sent them. */
export function readRoleChoices(data) {
    if (data?.roleChoiceRequired !== true || !Array.isArray(data.roles)) {
        return [];
    }
    return data.roles
        .filter((choice) => CHOICE_ROLES.includes(choice?.role)
        && (choice.tenantId === null || typeof choice.tenantId === 'string'))
        .map((choice) => ({ role: choice.role, tenantId: choice.tenantId }));
}
/** "Manager", or "Manager · maple" when the choices are in more than one organization. */
export function roleChoiceLabel(choice, choices) {
    const tenants = new Set(choices.map(item => item.tenantId));
    return tenants.size > 1 && choice.tenantId ? `${CHOICE_LABELS[choice.role]} · ${choice.tenantId}` : CHOICE_LABELS[choice.role];
}
/**
 * What the operations directory's answer means. `status` is null when the
 * directory could not be reached.
 *
 * - 2xx with a role choice: the password matches more than one role of one
 *   person. Ask which role, then sign in again with that role.
 * - 2xx with a provider handoff: sign in through vrs-server as that role.
 * - 2xx without one: a manager session, complete it.
 * - Suspended, inactive, rate limited (any other 4xx): the directory's answer
 *   stands.
 * - 401 or unreachable: try vrs-server's interpreter, then captioner, sign-in.
 *   An account created in vrs-server, or one whose email the directory holds
 *   as a manager, has no directory row of its role, and a copy written before
 *   vrs-server kept the directory in step can be stale
 *   (vrs-server/src/db/provider-directory.ts). vrs-server's is right.
 */
export function planProviderSignIn(status, data) {
    const handoffRole = data?.providerHandoff?.role;
    if (status !== null && status >= 200 && status < 300) {
        // One password, more than one role: the person chooses. No token yet.
        if (data?.roleChoiceRequired === true) {
            const roles = readRoleChoices(data);
            return roles.length
                ? { kind: 'choose', roles }
                : { error: 'Sign-in did not return a provider session.', kind: 'refused' };
        }
        // A correct manager password with the second step: no token yet.
        if (data?.totpRequired === true) {
            return typeof data.challengeId === 'string' && data.challengeId
                ? { challengeId: data.challengeId, kind: 'totp' }
                : { error: 'Sign-in did not return a provider session.', kind: 'refused' };
        }
        if (data?.totpEnrollmentRequired === true) {
            return typeof data.enrollmentTicket === 'string' && data.enrollmentTicket
                ? { kind: 'totp-enroll', ticket: data.enrollmentTicket }
                : { error: 'Sign-in did not return a provider session.', kind: 'refused' };
        }
        if (!handoffRole) {
            return { kind: 'manager' };
        }
        return PROVIDER_ROLES.includes(handoffRole)
            ? { error: '', kind: 'provider', roles: [handoffRole] }
            : { error: 'Sign-in did not return a provider session.', kind: 'refused' };
    }
    if (status !== null && status !== 401 && status < 500) {
        return { error: data?.error || 'Authentication failed.', kind: 'refused' };
    }
    return { error: data?.error || '', kind: 'provider', roles: [...PROVIDER_ROLES] };
}
function jobsSide(url) {
    const query = url.split('#')[0].split('?')[1] || '';
    return new URLSearchParams(query).get('side') === 'interpreter' ? 'interpreter' : '';
}
/**
 * Where a destination link goes: back to the page a signed-out visit came
 * from when that page is this destination (same page, same side of Jobs),
 * otherwise to the destination itself.
 */
export function destinationHref(destination, nextTarget) {
    if (nextTarget && nextTarget.split(/[?#]/)[0] === destination.href.split(/[?#]/)[0]
        && jobsSide(nextTarget) === jobsSide(destination.href)) {
        return nextTarget;
    }
    return destination.href;
}
/**
 * The page a signed-out visit came from (`?next=`), query and hash kept, when
 * it is exactly one of the provider destinations. Anything else is ignored.
 */
export function providerNextTarget(search, destinations) {
    const next = new URLSearchParams(search).get('next');
    if (next && destinations.some(path => next === path || next.startsWith(`${path}?`) || next.startsWith(`${path}#`))) {
        return next;
    }
    return null;
}
/**
 * The one-time sign-in hand-off from the MalkaVRS Terp tab (client-profile.js
 * `openTerp`): `#handoff=CODE&verifier=VERIFIER`, both base64url. Anything
 * else is not a hand-off. The page removes the fragment before it uses them.
 */
export function readHandoffFragment(hash) {
    const text = String(hash || '').replace(/^#/, '');
    if (!text.startsWith('handoff=')) {
        return null;
    }
    const fields = new Map();
    for (const pair of text.split('&')) {
        const split = pair.indexOf('=');
        if (split <= 0) {
            continue;
        }
        try {
            fields.set(pair.slice(0, split), decodeURIComponent(pair.slice(split + 1)));
        }
        catch {
            return null;
        }
    }
    const code = fields.get('handoff') || '';
    const verifier = fields.get('verifier') || '';
    return /^[A-Za-z0-9_-]{43}$/.test(code) && /^[A-Za-z0-9_-]{43,128}$/.test(verifier) ? { code, verifier } : null;
}
